import { FieldType } from 'bindings/forms/FieldType';
import close from 'images/icons/orders/close.svg';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { isAddressField } from '../../atoms/forms';
import { AUsers } from '../../atoms/global/users';
import PageLoader, { PageLoaderColors } from '../../components_v2/pageLoader/PageLoader';
import { Loader } from '../../styles/global/css/GlobalLoader';
import { SidelyBlack } from '../../styles/global/css/Utils';
import { Translate } from '../../styles/global/translate';
import { DateAccessor } from '../client-companies/data/CompanyColumns';
import { BlackContainer, Close, ImageSwapper, PhotoCounter } from '../gallery/style';
import { getPhotoFromHash } from '../gallery_v2/actions';
import { LoadingStateEnum } from '../import/model';
import { FlexDiv } from '../products/style';
import { formatCurrency } from '../reports/utils';
import { ColumnDisplayOptions, booleanColorsOf, standaloneColor } from '../map/colorScale';
import { getClientCompanyByUuid, Metadata } from './actions';

function CompanyDisplayer(props: { value: string }) {
	const [company, setCompany] = React.useState<string>();
	React.useEffect(() => {
		getClientCompanyByUuid(props.value).then(e => setCompany(e.company_name));
	}, []);
	return <>{company}</>;
}

export async function getImageAndHash(image: string, newImages: { [key: string]: { original?: string, thumb?: string } }) {
	let index = image.indexOf('thumbs/');
	if (index === 0) {
		const webpIndex = image.lastIndexOf('.webp');
		const hash = image.slice(index + 7, webpIndex);
		if (!newImages[hash]) newImages[hash] = {};
		if (newImages[hash].thumb) return;
		const { original, thumb } = await getPhotoFromHash({ hash: hash + '.webp', thumb: true, original: !newImages[hash].original });
		if (original) newImages[hash].original = original;
		if (thumb) newImages[hash].thumb = thumb;
		return;
	} else if (index !== -1) {
		const webpIndex = image.lastIndexOf('.webp');
		const hash = image.slice(index + 7, webpIndex);
		if (!newImages[hash]) newImages[hash] = {};
		newImages[hash].thumb = image;
		return;
	}
	index = image.indexOf('originals/');
	if (index === 0) {
		const webpIndex = image.lastIndexOf('.webp');
		const hash = image.slice(index + 10, webpIndex);
		if (!newImages[hash]) newImages[hash] = {};
		if (newImages[hash].original) return;
		const { original, thumb } = await getPhotoFromHash({ hash: hash + '.webp', thumb: !newImages[hash].thumb, original: true });
		if (original) newImages[hash].original = original;
		if (thumb) newImages[hash].thumb = thumb;
		return;
	} else if (index !== -1) {
		const webpIndex = image.lastIndexOf('.webp');
		const hash = image.slice(index + 10, webpIndex);
		if (!newImages[hash]) newImages[hash] = {};
		newImages[hash].original = image;
		return;
	}
	if (!newImages[image]) newImages[image] = {};
	if (newImages[image].thumb && newImages[image].original) return;
	const { original, thumb } = await getPhotoFromHash({ hash: image, thumb: !newImages[image].thumb, original: !newImages[image].original, old_path: true });
	if (original) newImages[image].original = original;
	if (thumb) newImages[image].thumb = thumb;
}

function ImageDisplayer(props: { value: string | string[] }) {
	const [modal, setModal] = React.useState<number>();
	const [images, setImages] = React.useState<{ [key: string]: { original?: string, thumb?: string } }>({});
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();

	React.useEffect(() => {
		const api = async() => {
			const newImages: { [key: string]: { original?: string, thumb?: string } } = {};
			setLoadingState(LoadingStateEnum.LOADING);
			let newValue;
			try {
				if (typeof props.value == 'string') newValue = JSON.parse(props.value);
				else newValue = props.value;
			} catch (_e) {
				if (typeof props.value == 'string' && props.value.includes(',')) newValue = props.value.split(',');
				else newValue = props.value;
			}
			if (Array.isArray(newValue)) {
				for (const image of newValue) {
					await getImageAndHash(image, newImages);
				}
			} else {
				await getImageAndHash(newValue, newImages);
			}
			setImages(newImages);
			setLoadingState(undefined);
		};
		api();
	}, [JSON.stringify(props.value)]);

	const mapped = Object.values(images);
	return <>
		{modal !== undefined && <OriginalImageDisplayer images={mapped.map(i => i.original ?? i.thumb ?? '')} onClickOut={() => setModal(undefined)} />}
		{loadingState == LoadingStateEnum.LOADING && <Loader width='30px' />}
		{mapped.length > 0 && <CardeImageDisplayerWrapper><CardImageDisplayer images={mapped.map((i) => i.thumb ?? i.original ?? '')} onClick={() => setModal(0)} /></CardeImageDisplayerWrapper>}
	</>;
}

function OriginalImageDisplayer(props: { images: string[], onClickOut: () => void, defaultIndex?: number }) {
	const [index, setIndex] = React.useState<number>(props.defaultIndex ?? 0);

	const changeIndex = (newIndex: number) => (e: React.MouseEvent<HTMLDivElement, MouseEvent> | KeyboardEvent) => {
		e.stopPropagation();
		if (newIndex < 0 || newIndex > props.images.length - 1) return;
		setIndex(newIndex);
	};

	function keyHandler(event: KeyboardEvent) {
		switch (event.key) {
			case 'Escape':
				props.onClickOut();
				return;
			case 'ArrowLeft':
				changeIndex(index - 1)(event);
				return;
			case 'ArrowRight':
				changeIndex(index + 1)(event);
		}
	}

	React.useEffect(() => {
		document.addEventListener('keydown', keyHandler, false);
		return () => {
			document.removeEventListener('keydown', keyHandler, false);
		};
	}, [index]);

	return <BlackContainer onClick={props.onClickOut}>
		<div style={{ height: '90%', display: 'flex', justifyContent: 'center' }}>
			{props.images.length > 1 && <ImageSwapper onClick={changeIndex(index - 1)} />}
			<img
				key={`IMAGE${props.images[index]}`}
				height='100%'
				src={props.images[index]}
				alt="" style={{ zIndex: 1, maxWidth: '90%', objectFit: 'cover', minWidth: 50 }} onClick={(e) => e.stopPropagation()} />
			{props.images.length > 1 && <ImageSwapper right onClick={changeIndex(index + 1)} />}
		</div>
		<div style={{ position: 'absolute' }}>
			<PageLoader color={PageLoaderColors.White} size={50} />
		</div>
		<Close src={close} onClick={props.onClickOut} />
	</BlackContainer>;

}

const CardeImageDisplayerWrapper = styled.div`
	border-radius: 5px;
	height: 200px;
	width: 200px;
	overflow: hidden;
    box-shadow: 0px 0px 7px color-mix(in oklab, ${SidelyBlack} 8%, transparent);
`;

const CardImageDisplayerDiv = styled.div`
	display: grid;
	grid-template-rows: 1fr 1fr;
	grid-template-columns: 1fr 1fr;
	height: 100%;
	width: 100%;
	overflow: hidden;
	column-gap: 4px;
	row-gap: 4px;
	position: relative;
	cursor: pointer;
`;
const gridPosition = [
	[],
	['1 / 1 / 3 / 3'],
	['1 / 1 / 3 / 2', '1 / 2 / 3 / 2'],
	['1 / 1 / 2 / 2', '2 / 1 / 3 / 2', '1 / 2 / 3 / 2'],
	['1 / 1 / 2 / 2', '2 / 1 / 3 / 2', '1 / 2 / 2 / 2', '2 / 2 / 2 / 2']
];
const CardImageDisplayerContainer = styled.div<{ nb: number, index: number }>`
	overflow: hidden;
	grid-area: ${({ index, nb }) => gridPosition[nb][index]};
`;

const CardImageDisplayerImage = styled.img`
	object-fit: cover;
	height: 100%;
	width: 100%;
`;

export function CardImageDisplayer(props: { images: string[], onClick?: () => void }) {
	const t = props.images.slice(0, 4);
	const nb = t.length;
	return <CardImageDisplayerDiv onClick={props.onClick}>
		{t.map((image, index) => <CardImageDisplayerContainer key={`IMAGE[${index}]`} nb={nb} index={index}><CardImageDisplayerImage src={image} /></CardImageDisplayerContainer>)}
		<PhotoCounter len={props.images.length}><div style={{ marginTop: 1 }}>{props.images.length}</div></PhotoCounter>
	</CardImageDisplayerDiv>;
}

const ColoredDot = styled.div<{ color: string }>`
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background-color: ${({ color }) => color};
`;

const BeforeText = styled.div<{ before: string }>`
	&::before {
		content: '${({ before }) => before}';
		padding-right: 6px;
	}
`;

export function FieldDisplayer(props: {
	field_type: FieldType,
	value,
	metadata?: Metadata,
	fieldId: number,
	justifyLeft?: boolean,
	// the map colours of the field when it is pinned on the company, shown
	// beside the value when they tell by the value alone
	displayOptions?: ColumnDisplayOptions,
}) {
	const users = useRecoilValue(AUsers);
	const valueColor = standaloneColor(props.displayOptions, props.field_type, props.value);
	const dot = valueColor ? <ColoredDot color={valueColor} /> : undefined;
	switch (props.field_type) {
		case 'Text':
			return <>{props.value}</>;
		case 'Number':
		case 'Integer':
		case 'Money': {
			const text = props.field_type === 'Money' ? formatCurrency(props.value / 100) : props.value;
			const inner = dot ? <FlexDiv gap='6px' align='center'>{dot}{text}</FlexDiv> : text;
			return props.justifyLeft ? <>{inner}</> : <FlexDiv width='100%' justify='center'>{inner}</FlexDiv>;
		}
		case 'Date':
			return <FlexDiv gap='6px' align='center'>{dot}<span>📅</span><DateAccessor date={props.value} /></FlexDiv>;
		case 'Boolean': {
			let res;
			if (typeof props.value == 'boolean') res = props.value;
			else if (typeof props.value == 'string') res = props.value.toLowerCase() === 'true';
			else return <></>;
			return <FlexDiv gap='6px'><ColoredDot color={booleanColorsOf(props.displayOptions)[res ? 'true' : 'false']} /><Translate id={res ? 'yes' : 'no'} /></FlexDiv>;
		}
		case 'Company':
			return <CompanyDisplayer value={props.value} />;
		case 'Url':
			return <><a href={props.value.toString()}>{props.value}</a></>;
		case 'Email':
			return <BeforeText before='📧'>{props.value}</BeforeText>;
		case 'Phone':
			return <BeforeText before='📞'>{props.value}</BeforeText>;
		case 'Address': {
			const t = props.value;
			if (!t || !isAddressField(t)) return <></>;
			return <>{t.streetNumber} {t.route}, {t.locality}, {t.country}</>;
		}
		case 'Location':
			return <BeforeText before='📍'>{props.value}</BeforeText>;
		case 'TextArea':
			return <>{props.value}</>;
		case 'Select':
			return <>{props.value}</>;
		case 'CheckIn': {
			const check_in = props.value ? true : false;
			return <>{<Translate id={check_in ? 'true' : 'false'} />}</>;

		}
		case 'Multiselect': {
			if (Array.isArray(props.value)) {
				return <>{props.value.map((e: string, i: number) => <React.Fragment key={i}>{e}<br /></React.Fragment>)}</>;
			}
			break;
		}
		case 'User':
			return <BeforeText before='👤'>{users.find(e => e.id.toString() == props.value?.toString())?.name}</BeforeText>;
		case 'Image': return <ImageDisplayer value={props.value} />;
		case 'Signature': return <SignatureField value={props.value} />;
		default:
			return <><Translate id={'display_coming_soon'} /></>;
	}

	return <></>;
}

function SignatureField(props: { value: string }) {
	return <CardeImageDisplayerWrapper>
		<div dangerouslySetInnerHTML={{ __html: props.value }} />
	</CardeImageDisplayerWrapper>;
}
